import { Button, Grid, IconButton } from '@mui/material';
import { FieldTemplate, Form, FormTemplate, Patient, Visit } from 'api/models';
import { FormikValues, useFormik } from 'formik';
import { getFormValidationSchema } from 'forms/Form';
import { mutation } from 'operations';
import { useTranslation } from 'react-i18next';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import LayoutFieldRenderer from 'routes/Form/LayoutFieldRenderer';
import {
  ToastSeverity,
  dialogState,
  projectState,
  toastStateSelector,
} from 'state';
import { DialogTypes, GeneralIds, ModelTypes } from 'types/types';
import {
  duplicateLayoutItem,
  getFields,
  getFieldTemplateIdsOfLayoutField,
  getLayoutRepeatIndex,
  isRepeatButtonVisible,
} from './utils';
import { FormFieldRenderer } from './FormFieldRenderer';
import FormLoadingIndicator from './FormLoadingIndicator';
import {
  LayoutItem,
  LayoutItemSubType,
  LayoutItemType,
} from 'api/models/FormTemplate';
import { DividerField } from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/DividerField';
import { FormStatus } from 'api/models';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import SelectableTableField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/SelectableTable/SelectableTableField';
import { getFieldsOfSelectableTable } from 'routes/FormTemplateEditor/FormEditor/utils';
import { formLayoutItemsState, lastTimeFormWasSaved } from 'state/formState';
import FormControls from './components/FormControls';
import CommentButton from './components/CommentButton';
import { Add, Delete } from '@mui/icons-material';
import { getId } from 'utils';
import { produce } from 'immer';
import { insert, get, set } from 'object-path';
import { FieldImportMeta } from 'api/models/Form';
import useCalculatedFieldCalculations from 'hooks/useCalculatedFieldCalculations';
import { useAutoSaveFormLayout } from './hooks/useAutoSaveFormLayout';

export interface FormValues {
  [key: string]: string | number | boolean | string[] | null;
}

export interface onRepeatFieldProps {
  parentPath: string;
  item: LayoutItem;
  insertIndex: number;
}

export interface onSaveProps {
  values: FormikValues;
  status: FormStatus;
  showToast?: boolean;
}

interface FormLayoutProps {
  initialValues: {
    [key: string]: string | number | boolean | string[] | null;
  };
  formTemplate?: FormTemplate;
  fieldTemplates: FieldTemplate[];
  isReadOnly: boolean;
  patient?: Patient;
  visit?: Visit;
  mode: 'create' | 'edit' | 'view';
  formStatus?: FormStatus;
  externalFieldValues: {
    [key: string]: string | number | boolean | string[] | null;
  };
  importedFieldMap?: Map<string, FieldImportMeta>;
}

export default function FormLayout({
  initialValues,
  formTemplate,
  fieldTemplates,
  isReadOnly,
  patient,
  visit,
  mode,
  formStatus,
  externalFieldValues,
  importedFieldMap,
}: FormLayoutProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setToast = useSetRecoilState(toastStateSelector);
  const openDialog = useSetRecoilState(dialogState);
  const { selectedProject } = useRecoilValue(projectState);
  const { visitId, formId } = useParams<GeneralIds>();
  const setLastSaved = useSetRecoilState(lastTimeFormWasSaved);
  const [shouldShowLeaveConfirmation, setShouldShowLeaveConfirmation] =
    useState(true);
  const [layoutItems, setLayoutItems] = useRecoilState(formLayoutItemsState);
  const [currentStatus, setCurrentStatus] = useState(formStatus);

  let blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      currentLocation.pathname !== nextLocation.pathname &&
      shouldShowLeaveConfirmation &&
      currentStatus === FormStatus.New
    );
  });

  const onSave = async ({ values, showToast, status }: onSaveProps) => {
    try {
      const fields = getFields(values, fieldTemplates, layoutItems);
      const { data: form } = await mutation<Form>({
        type: ModelTypes.form,
        body: {
          id: formId ?? '',
          status,
          formTemplateId: formTemplate?.id ?? '',
          fields,
        },
        ids: {
          visitId,
          id: formId ?? '',
        },
      });

      setCurrentStatus(status);

      if (status === FormStatus.Finalized) {
        setShouldShowLeaveConfirmation(false);
      }

      setLastSaved(form?.updatedAt);

      if (showToast) {
        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
        if (status === FormStatus.Finalized) {
          navigate(-1);
        }
      }
      return formId;
    } catch (e: any) {
      const errorCode = e.response?.data?.error?.[0].code;
      showToast &&
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
    }
  };

  const onRepeatField = ({
    parentPath,
    item,
    insertIndex,
  }: onRepeatFieldProps) => {
    const latestIndex =
      Object.keys(formik.values)
        .map(id =>
          id.includes(item.fieldTemplateId ?? '')
            ? parseInt(id.split(':')[1])
            : null,
        )
        .filter(Boolean)
        .sort((a, b) => b! - a!)[0] ?? 0;

    const items = produce(layoutItems, draft => {
      insert(
        draft,
        parentPath,
        {
          ...item,
          id: getId(),
          fieldTemplateId: `${item.fieldTemplateId?.split(':')[0]}:${
            latestIndex + 1
          }`,
        },
        insertIndex + 1,
      );
    });

    setLayoutItems(items);
    formik.setValues({
      ...formik.values,
      [`${item.fieldTemplateId}:${latestIndex + 1}`]: '',
    });
    onSave({
      values: formik.values,
      showToast: false,
      status: FormStatus.Filling,
    });
  };

  const onRepeatableFieldDelete = ({
    parentPath,
    item,
    insertIndex,
  }: onRepeatFieldProps) => {
    const items = produce(layoutItems, draft => {
      const parent = get(draft, parentPath);
      parent.splice(insertIndex, 1);
      set(draft, parentPath, parent);
    });

    setLayoutItems(items);
    formik.setValues(
      Object.keys(formik.values).reduce((acc, key) => {
        if (!key.includes(item.fieldTemplateId ?? '')) {
          acc[key] = formik.values[key];
        }
        return acc;
      }, {} as FormValues),
    );
    onSave({
      values: formik.values,
      showToast: false,
      status: FormStatus.Filling,
    });
  };

  const onRepeatLayoutField = ({
    parentPath,
    item,
    insertIndex,
  }: onRepeatFieldProps) => {
    const index = getLayoutRepeatIndex(layoutItems, item.id);
    const layoutRepeatIndex = index === 0 ? 1 : index + 1;

    const items = produce(layoutItems, draft => {
      insert(
        draft,
        parentPath,
        duplicateLayoutItem(item, layoutRepeatIndex),
        insertIndex + 1,
      );
    });

    setLayoutItems(items);
    const fieldTemplateIds = getFieldTemplateIdsOfLayoutField(item);
    formik.setValues({
      ...formik.values,
      ...fieldTemplateIds.reduce((r, id) => {
        r[`${id}|${layoutRepeatIndex}`] = '';
        return r;
      }, {} as any),
    });
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: getFormValidationSchema({
      templates: fieldTemplates,
      patient,
      visit,
    }),
    onSubmit: values =>
      onSave({
        values: values,
        showToast: true,
        status: FormStatus.Finalized,
      }),
  });

  useAutoSaveFormLayout(formik, fieldTemplates, mode, onSave, currentStatus);

  useCalculatedFieldCalculations({
    setFormikState: formik.setFormikState,
    templates: fieldTemplates,
  });

  useEffect(() => {
    // this is blocking the user from leaving the page if the form is not saved yet
    if (blocker.state === 'blocked') {
      openDialog({
        isOpen: true,
        dialogType: DialogTypes.formConfirmation,
        data: {
          formId: formId,
          blocker,
          values: formik.values,
        },
      });
    }
  }, [blocker, formId, openDialog, shouldShowLeaveConfirmation, formik.values]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        formik.handleSubmit();
      }}
    >
      <Grid container spacing={2} mt={2} pl={2}>
        {layoutItems.map((item, index) => {
          const field = fieldTemplates.find(
            // we need to split here because of the repeatable field index
            field => field.id === item.fieldTemplateId?.split(':')[0],
          );

          if (item.type === LayoutItemType.Container) {
            if (item.subType === LayoutItemSubType.Layout) {
              return (
                <Box
                  width="100%"
                  display="flex"
                  alignItems={'flex-end'}
                  flexDirection={'column'}
                  key={`layout-field-${item.id}-${index}`}
                >
                  <LayoutFieldRenderer
                    item={item}
                    fieldTemplates={fieldTemplates}
                    formik={formik}
                    isReadOnly={isReadOnly}
                    externalFieldValues={externalFieldValues}
                    onRepeatField={onRepeatField}
                    onRepeatableFieldDelete={onRepeatableFieldDelete}
                    path={`${index}`}
                  />
                  {item.layoutOptions?.isRepeatable &&
                    (item.layoutOptions.repeatIndex ?? 0) < 1 &&
                    !item.layoutOptions.replicaOf && (
                      <Button
                        sx={{
                          maxWidth: 'min-content',
                          flexDirection: 'row',
                          display: 'flex',
                          mt: 1,
                        }}
                        onClick={() =>
                          onRepeatLayoutField({
                            parentPath: '',
                            insertIndex: index,
                            item,
                          })
                        }
                        size="small"
                      >
                        <Add fontSize="small" /> {t('forms.repeat')}
                      </Button>
                    )}
                </Box>
              );
            }
            if (item.subType === LayoutItemSubType.SelectableTable) {
              const fields = getFieldsOfSelectableTable({
                layoutItems: item.children ?? [],
                fieldTemplates,
              });
              return (
                <SelectableTableField
                  key={`layout-field-selectable-table-${item.id}`}
                  item={item}
                  fields={fields}
                  formik={formik}
                  isReadOnly={isReadOnly}
                  externalFieldValues={externalFieldValues}
                />
              );
            }
          }

          if (item.type === LayoutItemType.Static)
            return (
              <DividerField key={`layout-field-${item.id}`} layoutItem={item} />
            );

          if (field)
            return (
              <Box
                display={'flex'}
                mt={1}
                mb={1}
                width={'100%'}
                key={`form-field-${item.fieldTemplateId}`}
                flexDirection={'row'}
                alignItems={'start'}
              >
                <Box
                  width="100%"
                  display="flex"
                  alignItems={'flex-end'}
                  flexDirection={'column'}
                >
                  <FormFieldRenderer
                    field={field}
                    valueId={item.fieldTemplateId}
                    formik={formik}
                    isReadOnly={isReadOnly}
                    externalFieldValues={externalFieldValues}
                    importedFieldMap={importedFieldMap}
                  />
                  {isRepeatButtonVisible(layoutItems, item, field) && (
                    <Button
                      sx={{
                        maxWidth: 'min-content',
                        flexDirection: 'row',
                        display: 'flex',
                        mt: 1,
                      }}
                      onClick={() =>
                        onRepeatField({
                          parentPath: '',
                          insertIndex: index,
                          item,
                        })
                      }
                      size="small"
                    >
                      <Add fontSize="small" /> {t('forms.repeat')}
                    </Button>
                  )}
                </Box>
                {isReadOnly &&
                  selectedProject?.configuration?.queryModuleEnabled && (
                    <CommentButton
                      fieldId={item.fieldTemplateId!}
                      value={formik.values[item.fieldTemplateId!]}
                    />
                  )}

                {field.fieldAttributes.isRepeatable &&
                  item.fieldTemplateId &&
                  parseInt(item.fieldTemplateId?.split(':')?.[1]) > 0 && (
                    <IconButton
                      size="small"
                      sx={{ ml: 1, mt: 2 }}
                      onClick={() =>
                        onRepeatableFieldDelete({
                          parentPath: '',
                          insertIndex: index,
                          item,
                        })
                      }
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
              </Box>
            );
          return null;
        })}
      </Grid>

      {formik.isSubmitting && <FormLoadingIndicator />}

      <FormControls
        setShouldShowLeaveConfirmation={setShouldShowLeaveConfirmation}
        onSave={onSave}
        formik={formik}
        patient={patient}
        fieldTemplates={fieldTemplates}
        readOnly={mode === 'view'}
      />
    </form>
  );
}
